import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./ECE.module.css";
import eceData1 from "./ECEData1";
import eceData2 from "./ECEData2";

const allQuizData = [...eceData1, ...eceData2];

const getRandomQuestions = (data, count = 25) => {
  const easy = data.filter((q) => q.level === "easy");
  const medium = data.filter((q) => q.level === "medium");
  const hard = data.filter((q) => q.level === "hard");

  const pickRandom = (arr, n) =>
    arr.sort(() => 0.5 - Math.random()).slice(0, n);

  let selected = [
    ...pickRandom(easy, 4),
    ...pickRandom(medium, 4),
    ...pickRandom(hard, 4),
  ];

  if (selected.length < count) {
    const remaining = data.filter((q) => !selected.includes(q));
    selected = [...selected, ...pickRandom(remaining, count - selected.length)];
  }

  // Shuffle selected questions and their options
  return selected
    .sort(() => 0.5 - Math.random())
    .map((q) => ({
      ...q,
      options: [...q.options].sort(() => 0.5 - Math.random()),
    }));
};

const ECE = () => {
  const navigate = useNavigate();
  const [quizData, setQuizData] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [error, setError] = useState("");
  const [showRules, setShowRules] = useState(true);
  const [disqualified, setDisqualified] = useState(false);

  const timerRef = useRef(null);

  // Check session storage on mount
  useEffect(() => {
    const status = sessionStorage.getItem("quiz_status_ece");
    if (status === "disqualified") {
      setDisqualified(true);
      setShowRules(false);
    } else if (status === "completed") {
      setShowResults(true);
      setShowRules(false);
      const savedScore = sessionStorage.getItem("quiz_score_ece") || 0;
      setAnswers({ score: parseInt(savedScore, 10) });
    } else if (status === "started") {
      // Refreshing during a started quiz automatically disqualifies
      setDisqualified(true);
      sessionStorage.setItem("quiz_status_ece", "disqualified");
      setShowRules(false);
    }
  }, []);

  // Load quiz after rules are accepted
  useEffect(() => {
    if (!showRules && !disqualified && !showResults) {
      const selected = getRandomQuestions(allQuizData, 25);
      setQuizData(selected);
    }
  }, [showRules, disqualified, showResults]);

  // Timer + Disqualification logic
  useEffect(() => {
    if (quizData.length === 0 || showResults || disqualified) return;

    clearInterval(timerRef.current);
    setTimeLeft(10);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);

          // Move to next next or submit if no answer given
          if (!answers[currentQuestion]) {
            if (currentQuestion < quizData.length - 1) {
              setCurrentQuestion((q) => q + 1);
            } else {
              handleSubmit();
            }
            return 0;
          }

          // Move to next or submit
          if (currentQuestion < quizData.length - 1) {
            setCurrentQuestion((q) => q + 1);
          } else {
            handleSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, quizData, showResults, disqualified]);

  const handleOptionSelect = (option) => {
    if (timeLeft > 0) {
      setAnswers((prev) => ({ ...prev, [currentQuestion]: option }));
      setError("");
    }
  };

  const handleNext = () => {
    if (answers[currentQuestion] === undefined) {
      setError("Please select an answer before proceeding.");
      return;
    }
    setError("");
    setCurrentQuestion((q) => q + 1);
  };

  const handleSubmit = () => {
    clearInterval(timerRef.current);
    let score = 0;
    quizData.forEach((q, i) => {
      if (answers[i] === q.answer) score++;
    });
    setShowResults(true);
    setAnswers((prev) => ({ ...prev, score }));
    sessionStorage.setItem("quiz_status_ece", "completed");
    sessionStorage.setItem("quiz_score_ece", score);
  };

  // RULES SCREEN
  if (showRules) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.rulesModal}>
          <h2 className={styles.rulesTitle}>Quiz Rules</h2>
          <ul className={styles.rulesList}>
            <li>
              1️⃣ You have <b>10 seconds</b> per question.
            </li>
            <li>2️⃣ You can change answers before time ends.</li>
            <li>3️⃣ All questions are mandatory.</li>
            <li>
              4️⃣ Fail to answer ➜ <b>Move to next question ⏩</b>
            </li>
            <li>
              🎁 <b>Score 23, 24, or 25 marks to get an appreciation gift!</b>
            </li>
          </ul>
          <button
            onClick={() => {
              sessionStorage.setItem("quiz_status_ece", "started");
              setShowRules(false);
            }}
            className={styles.startButton}
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  // DISQUALIFIED SCREEN
  if (disqualified) {
    return (
      <div className={styles.quizContainer}>
        <motion.div
          className={styles.quizCard}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className={styles.resultsTitle}>❌ You Are Disqualified</h2>
          <p className={styles.congratsText}>Please try again later or contact support.</p>
          <button onClick={() => navigate("/")} className={styles.submitButton}>
            Go to Home 🏠
          </button>
        </motion.div>
      </div>
    );
  }

  // LOADING SCREEN
  if (quizData.length === 0 && !disqualified && !showResults) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  const total = quizData.length || 12;
  const q = quizData[currentQuestion];
  const progress = ((currentQuestion + 1) / total) * 100;

  // MAIN QUIZ SCREEN
  return (
    <div className={styles.quizContainer}>
      <motion.div
        className={styles.quizCard}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {showResults ? (
          <div className={styles.resultsContainer}>
            <div className={styles.congratsIcon}>🎉</div>
            <h2 className={styles.resultsTitle}>Quiz Completed</h2>
            <div className={styles.codeDisplay}>
              <p className={styles.codeLabel}>Your Score:</p>
              <p className={styles.codeValue}>
                {answers.score}/{total}
              </p>
              <p className={styles.scoreMessage}>
                {answers.score <= 15 ? "Fair!" : answers.score <= 20 ? "Good!" : "Superb!"}
              </p>
            </div>
            <button
              onClick={() => navigate("/")}
              className={styles.submitButton}
            >
              Go Home
            </button>
          </div>
        ) : (
          <>
            <div className={styles.progressBarContainer}>
              <motion.div
                className={styles.progressBar}
                animate={{ width: `${progress}%` }}
              />
            </div>

            <div className={styles.questionHeader}>
              <span>
                Question {currentQuestion + 1}/{total}
              </span>
              <span className={styles.timer}>⏳ {timeLeft}s</span>
            </div>

            <h3 className={styles.questionText}>{q.question}</h3>

            <div className={styles.optionsContainer}>
              {q.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleOptionSelect(opt)}
                  className={`${styles.optionButton} ${
                    answers[currentQuestion] === opt
                      ? styles.selectedOption
                      : ""
                  }`}
                  disabled={timeLeft === 0}
                >
                  {opt}
                </button>
              ))}
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <div className={styles.navigationButtons}>
              {currentQuestion < total - 1 ? (
                <button onClick={handleNext} className={styles.submitButton}>
                  Next
                </button>
              ) : (
                <button onClick={handleSubmit} className={styles.submitButton}>
                  Submit
                </button>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ECE;
